import { flatten } from 'utils';
import messagesInEnglish from 'lang/en.json';
import messagesInEnglishGB from 'lang/en-GB.json';
import messagesInEnglishCA from 'lang/en-CA.json';
import messagesInEnglishIE from 'lang/en-IE.json';
import messagesInEnglishAU from 'lang/en-AU.json';
import messagesInGerman from 'lang/de-DE.json';
import messagesInSpanish from 'lang/es-ES.json';
import messagesInCanadianFrench from 'lang/fr-CA.json';
import messagesInFrench from 'lang/fr-FR.json';
import messagesInItalian from 'lang/it-IT.json';
import messagesInNorwegian from 'lang/nb-NO.json';
import messagesInDutch from 'lang/nl-NL.json';
import messagesInPolish from 'lang/pl.json';
import messagesInPortuguese from 'lang/pt-PT.json';
import messagesInHungarian from 'lang/hu-HU.json';
import messagesInSlovak from 'lang/sk-SK.json';
import { useMemo } from 'react';

export const DEFAULT_LOCALE = 'en';

// Locale keys are lowercase-hyphenated; getAvailableLocale normalizes user input
// (lowercases + converts underscores to hyphens) before lookup, so a canonical tag
// (e.g. `fr-FR`) resolves — see VIS-1695. Bare-language aliases (de, es, fr, it, nb/no,
// nl, pt, hu, sk) map a region-less locale onto a catalogue.
const messagesMap = new Map<string, Record<string, unknown>>([
  ['en', messagesInEnglish.wallet],
  ['en-gb', messagesInEnglishGB.wallet],
  ['en-ca', messagesInEnglishCA.wallet],
  ['en-ie', messagesInEnglishIE.wallet],
  ['en-au', messagesInEnglishAU.wallet],
  ['es-es', messagesInSpanish.wallet],
  ['es', messagesInSpanish.wallet],
  ['fr-fr', messagesInFrench.wallet],
  ['fr', messagesInFrench.wallet],
  ['fr-ca', messagesInCanadianFrench.wallet],
  ['de-de', messagesInGerman.wallet],
  ['de', messagesInGerman.wallet],
  ['nl-nl', messagesInDutch.wallet],
  ['nl', messagesInDutch.wallet],
  ['it-it', messagesInItalian.wallet],
  ['it', messagesInItalian.wallet],
  ['nb-no', messagesInNorwegian.wallet],
  ['nb', messagesInNorwegian.wallet],
  ['no', messagesInNorwegian.wallet],
  ['pl', messagesInPolish.wallet],
  ['pt-pt', messagesInPortuguese.wallet],
  ['pt', messagesInPortuguese.wallet],
  ['hu-hu', messagesInHungarian.wallet],
  ['hu', messagesInHungarian.wallet],
  ['sk-sk', messagesInSlovak.wallet],
  ['sk', messagesInSlovak.wallet],
]);

export const getAvailableLocale = (userLocale: string): [string, string] => {
  const normalized = userLocale.toLowerCase().replace(/_/g, '-');

  if (messagesMap.has(normalized)) {
    return [normalized, normalized];
  }

  const [lang] = normalized.split('-');

  if (messagesMap.has(lang)) {
    return [lang, normalized];
  }

  return [DEFAULT_LOCALE, DEFAULT_LOCALE];
};

interface LocaleData {
  messages: Record<string, string>;
  locale: string;
}

export const useLocaleData = (userLocale: string): LocaleData => {
  return useMemo(() => {
    const [messagesKey, locale] = getAvailableLocale(userLocale);

    return {
      locale,
      messages: flatten(messagesMap.get(messagesKey)) as Record<string, string>,
    };
  }, [userLocale]);
};
